import { useMemo } from 'react';
import classNames from 'classnames';

import { TExtendedCollectionRankingEntry } from '@lootboy/lib/slices/cardCollections/types';
import { NOOP } from '@lootboy/lib/utils';

import ButtonClean from '~/components/ButtonClean';

import PositionChangeIcon from '~/views/CollectionView/CollectionRanking/CollectionRankingEntry/PositionChangeIcon';
import { RankingEntryAvatar } from '~/views/CollectionView/CollectionRanking/CollectionRankingEntry/RankingEntryAvatar';

import { ReactComponent as ClockIcon } from '~/assets/images/iconsLibrary/clock.svg';

import styles from './CollectionRankingEntry.module.scss';

export type TCollectionRankingEntryProps = {
  rankingEntry: TExtendedCollectionRankingEntry;
  onClick?: () => void;
  showRadarIcon?: boolean;
  onPositionIconClick?: () => void;
};

export function CollectionRankingEntry({
  rankingEntry,
  onClick,
  showRadarIcon = false,
  onPositionIconClick,
}: TCollectionRankingEntryProps): JSX.Element {
  const positionTextSize = useMemo(() => {
    if (rankingEntry.no < 100) {
      return 20;
    }
    if (rankingEntry.no < 10_000) {
      return 15;
    }
    if (rankingEntry.no < 1_000_000) {
      return 13;
    }
    return 11;
  }, [rankingEntry.no]);

  return (
    <div
      role={onClick ? 'button' : 'listitem'}
      className={classNames(
        styles.entryContainer,
        rankingEntry.isUserEntry && styles.userRanking,
        !onClick && styles.notClickable,
      )}
      onClick={onClick ?? NOOP}
    >
      <div className={styles.positionContainer}>
        <div
          className={styles.position}
          style={{ fontSize: `${positionTextSize}px` }}
        >
          {rankingEntry.no}
        </div>
        {onPositionIconClick ? (
          <ButtonClean
            onClick={(e) => {
              e.stopPropagation();
              onPositionIconClick?.();
            }}
            className={styles.desyncButton}
          >
            <ClockIcon className={styles.desyncIcon} />
          </ButtonClean>
        ) : (
          <PositionChangeIcon
            placement={rankingEntry.no}
            previousPlacement={rankingEntry.prevNo ?? 0}
            className={styles.positionChangeIcon}
          />
        )}
      </div>
      <RankingEntryAvatar
        avatarUrl={rankingEntry.avatar ?? ''}
        alt={`${rankingEntry.username} avatar`}
        showRadarIcon={showRadarIcon}
      />
      <p className={styles.username}>{rankingEntry.username}</p>
      <div className={styles.value}>{rankingEntry.value}</div>
    </div>
  );
}
